<script setup>
import { ref } from 'vue';
import { useI18n } from 'vue-i18n';
import {
  getCoreRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useVueTable,
} from '@tanstack/vue-table';
import Button from 'dashboard/components-next/button/Button.vue';
import { Button as ShadcnButton } from 'dashboard/components-shadcn/new-york/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from 'dashboard/components-shadcn/new-york/ui/select';

const props = defineProps({
  data: {
    type: Array,
    default: () => [],
  },
});

const emit = defineEmits(['sendBroadcast', 'delete']);

const { t } = useI18n();

const sorting = ref([]);
const pagination = ref({ pageIndex: 0, pageSize: 10 });

const STATUS_CLASSES = {
  approved:
    'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300',
  pending:
    'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300',
  rejected: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300',
};
const STATUS_ICONS = { approved: '✅', pending: '⏳', rejected: '❌' };
const CATEGORY_ICONS = { MARKETING: '📢', UTILITY: '🔧', AUTHENTICATION: '🔐' };
const LANGUAGE_FLAGS = { pt_BR: '🇧🇷', en_US: '🇺🇸', es_ES: '🇪🇸', fr_FR: '🇫🇷' };

const getStatusClass = status =>
  STATUS_CLASSES[status?.toLowerCase()] ||
  'bg-slate-100 text-slate-700 dark:bg-slate-700 dark:text-slate-300';

const getStatusIcon = status => STATUS_ICONS[status?.toLowerCase()] || '❔';
const getCategoryIcon = category =>
  CATEGORY_ICONS[category?.toUpperCase()] || '📝';
const getLanguageFlag = language => LANGUAGE_FLAGS[language] || '🌍';

const columns = [
  {
    accessorKey: 'name',
    header: () => t('CAMPAIGN.WHATSAPP_OFFICIAL.TABLE.NAME'),
    enableSorting: true,
  },
  {
    accessorKey: 'category',
    header: () => t('CAMPAIGN.WHATSAPP_OFFICIAL.TABLE.CATEGORY'),
    enableSorting: true,
  },
  {
    accessorKey: 'status',
    header: () => t('CAMPAIGN.WHATSAPP_OFFICIAL.TABLE.STATUS'),
    enableSorting: true,
  },
  {
    accessorKey: 'language',
    header: () => t('CAMPAIGN.WHATSAPP_OFFICIAL.TABLE.LANGUAGE'),
    enableSorting: true,
  },
  {
    accessorKey: 'inboxName',
    header: () => t('CAMPAIGN.WHATSAPP_OFFICIAL.TABLE.INBOX'),
    enableSorting: true,
  },
  {
    accessorKey: 'actions',
    header: () => t('CAMPAIGN.WHATSAPP_OFFICIAL.TABLE.ACTIONS'),
    enableSorting: false,
  },
];

const table = useVueTable({
  get data() {
    return props.data || [];
  },
  columns,
  state: {
    get sorting() {
      return sorting.value;
    },
    get pagination() {
      return pagination.value;
    },
  },
  onSortingChange: updater => {
    const next =
      typeof updater === 'function' ? updater(sorting.value) : updater;
    sorting.value = Array.isArray(next) ? next : [];
  },
  onPaginationChange: updater => {
    pagination.value =
      typeof updater === 'function' ? updater(pagination.value) : updater;
  },
  getCoreRowModel: getCoreRowModel(),
  getSortedRowModel: getSortedRowModel(),
  getPaginationRowModel: getPaginationRowModel(),
});
</script>

<template>
  <!-- eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text, vue/no-static-inline-styles -->
  <div class="flex flex-col flex-1 min-h-0">
    <!-- Scrollable table container -->
    <div
      class="flex-1 min-h-0 rounded-md bg-white dark:bg-slate-800 shadow-sm border border-slate-200 dark:border-slate-800 overflow-auto"
    >
      <table class="w-full min-w-[1000px]">
        <thead class="sticky top-0 z-10 bg-green-200 dark:bg-n-slate-2">
          <tr
            v-for="headerGroup in table.getHeaderGroups()"
            :key="headerGroup.id"
          >
            <th
              v-for="header in headerGroup.headers"
              :key="header.id"
              class="font-semibold text-green-800 dark:text-green-300 p-4 text-left border-b border-green-900/30 dark:border-green-800"
              :class="{
                'cursor-pointer select-none': header.column.getCanSort(),
              }"
              @click="
                header.column.getCanSort()
                  ? header.column.getToggleSortingHandler()?.($event)
                  : undefined
              "
            >
              <div class="flex items-center gap-2">
                <span>{{
                  header.isPlaceholder ? '' : header.column.columnDef.header()
                }}</span>
                <span v-if="header.column.getCanSort()">
                  <span v-if="header.column.getIsSorted() === 'asc'">↑</span>
                  <span v-else-if="header.column.getIsSorted() === 'desc'"
                    >↓</span
                  >
                  <span v-else>↕</span>
                </span>
              </div>
            </th>
          </tr>
        </thead>

        <tbody>
          <tr
            v-for="row in table.getRowModel().rows"
            :key="row.id"
            class="hover:bg-slate-50 dark:hover:bg-green-900/10"
          >
            <td
              v-for="cell in row.getVisibleCells()"
              :key="cell.id"
              class="p-3 border-b border-green-900/30 dark:border-green-900/30"
            >
              <!-- Name -->
              <div
                v-if="cell.column.id === 'name'"
                class="flex items-center gap-2"
              >
                <span class="text-lg">📝</span>
                <span class="font-medium text-slate-800 dark:text-white">
                  {{ cell.getValue() }}
                </span>
              </div>

              <!-- Category -->
              <div
                v-else-if="cell.column.id === 'category'"
                class="flex items-center gap-2"
              >
                <span class="text-base">{{
                  getCategoryIcon(cell.getValue())
                }}</span>
                <span class="text-slate-600 dark:text-slate-400">
                  {{
                    t(
                      `WHATSAPP_TEMPLATES.CATEGORY.${cell.getValue()?.toUpperCase()}`
                    ) || cell.getValue()
                  }}
                </span>
              </div>

              <!-- Status badge -->
              <span
                v-else-if="cell.column.id === 'status'"
                class="inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-semibold"
                :class="getStatusClass(cell.getValue())"
              >
                <span class="text-sm">{{
                  getStatusIcon(cell.getValue())
                }}</span>
                {{
                  t(
                    `WHATSAPP_TEMPLATES.STATUS.${cell.getValue()?.toUpperCase()}`
                  ) || t('WHATSAPP_TEMPLATES.STATUS.DEFAULT')
                }}
              </span>

              <!-- Language -->
              <div
                v-else-if="cell.column.id === 'language'"
                class="flex items-center gap-2"
              >
                <span class="text-base">{{
                  getLanguageFlag(cell.getValue())
                }}</span>
                <span class="text-slate-600 dark:text-slate-400">
                  {{
                    t(`WHATSAPP_TEMPLATES.LANGUAGES.${cell.getValue()}`) ||
                    cell.getValue()
                  }}
                </span>
              </div>

              <!-- Inbox -->
              <div
                v-else-if="cell.column.id === 'inboxName'"
                class="flex items-center gap-2"
              >
                <span class="text-base">📥</span>
                <span class="text-slate-600 dark:text-slate-400">
                  {{ cell.getValue() }}
                </span>
              </div>

              <!-- Actions -->
              <div
                v-else-if="cell.column.id === 'actions'"
                class="flex items-center gap-2"
              >
                <Button
                  v-tooltip.top="
                    row.original.status?.toLowerCase() === 'approved'
                      ? $t('CAMPAIGN.WHATSAPP_OFFICIAL.BUTTONS.SEND_BROADCAST')
                      : $t('WHATSAPP_TEMPLATES.STATUS.PENDING')
                  "
                  variant="faded"
                  color="slate"
                  size="xs"
                  icon="i-lucide-sliders-vertical"
                  :disabled="row.original.status?.toLowerCase() !== 'approved'"
                  @click="emit('sendBroadcast', row.original)"
                />
                <Button
                  v-tooltip.top="
                    $t('CAMPAIGN.WHATSAPP_OFFICIAL.BUTTONS.DELETE')
                  "
                  variant="faded"
                  color="ruby"
                  size="xs"
                  icon="i-lucide-trash"
                  @click="emit('delete', row.original)"
                />
              </div>

              <!-- Default fallback -->
              <span v-else>{{ cell.getValue() }}</span>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- Pagination — must be sibling of the scroll container, NOT inside it -->
    <div
      class="border border-slate-200 dark:border-slate-800 p-2 mb-2 rounded-md mt-4"
    >
      <div class="flex items-center justify-between px-2 gap-2">
        <!-- Records info -->
        <div class="text-sm text-n-slate-10 shrink-0">
          <span v-if="table.getFilteredRowModel().rows.length > 0">
            {{
              t('CAMPAIGN.WHATSAPP_OFFICIAL.PAGINATION.SHOWING', {
                from:
                  table.getState().pagination.pageIndex *
                    table.getState().pagination.pageSize +
                  1,
                to: Math.min(
                  (table.getState().pagination.pageIndex + 1) *
                    table.getState().pagination.pageSize,
                  table.getFilteredRowModel().rows.length
                ),
                total: table.getFilteredRowModel().rows.length.toLocaleString(),
              })
            }}
          </span>
          <span v-else>
            {{ t('CAMPAIGN.WHATSAPP_OFFICIAL.PAGINATION.NO_RECORDS') }}
          </span>
        </div>

        <!-- Page controls -->
        <div class="flex items-center gap-4 lg:gap-8">
          <!-- Page size selector -->
          <div class="flex items-center gap-2">
            <p
              class="text-sm font-medium text-n-slate-12 whitespace-nowrap mt-1"
            >
              {{ t('CAMPAIGN.WHATSAPP_OFFICIAL.PAGINATION.ROWS_PER_PAGE') }}
            </p>
            <Select
              :model-value="`${table.getState().pagination.pageSize}`"
              @update:model-value="value => table.setPageSize(Number(value))"
            >
              <SelectTrigger
                class="h-8 w-[70px] bg-n-slate-4 border-n-slate-6 text-n-slate-12"
              >
                <SelectValue
                  :placeholder="`${table.getState().pagination.pageSize}`"
                />
              </SelectTrigger>
              <SelectContent
                side="top"
                align="start"
                class="w-[70px] bg-n-slate-1 border-n-slate-6"
              >
                <SelectItem
                  v-for="size in [5, 10, 20, 30, 50]"
                  :key="size"
                  :value="`${size}`"
                >
                  {{ size }}
                </SelectItem>
              </SelectContent>
            </Select>
          </div>

          <!-- Page info -->
          <div
            class="flex items-center justify-center text-sm font-medium text-n-slate-12 whitespace-nowrap px-3"
          >
            {{
              t('CAMPAIGN.WHATSAPP_OFFICIAL.PAGINATION.PAGE_INFO', {
                current: table.getState().pagination.pageIndex + 1,
                total: table.getPageCount() || 1,
              })
            }}
          </div>

          <!-- Navigation buttons -->
          <div class="flex items-center gap-2">
            <ShadcnButton
              variant="outline"
              class="hidden h-8 w-8 p-0 lg:flex border-n-slate-6 hover:bg-n-slate-3"
              :disabled="!table.getCanPreviousPage()"
              :title="t('CAMPAIGN.WHATSAPP_OFFICIAL.PAGINATION.GO_TO_FIRST')"
              @click="table.setPageIndex(0)"
            >
              <span class="sr-only">{{
                t('CAMPAIGN.WHATSAPP_OFFICIAL.PAGINATION.GO_TO_FIRST')
              }}</span>
              <span class="i-lucide-chevrons-left h-4 w-4 text-n-slate-11" />
            </ShadcnButton>

            <ShadcnButton
              variant="outline"
              class="h-8 w-8 p-0 border-n-slate-6 hover:bg-n-slate-3"
              :disabled="!table.getCanPreviousPage()"
              :title="t('CAMPAIGN.WHATSAPP_OFFICIAL.PAGINATION.GO_TO_PREVIOUS')"
              @click="table.previousPage()"
            >
              <span class="sr-only">{{
                t('CAMPAIGN.WHATSAPP_OFFICIAL.PAGINATION.GO_TO_PREVIOUS')
              }}</span>
              <span class="i-lucide-chevron-left h-4 w-4 text-n-slate-11" />
            </ShadcnButton>

            <ShadcnButton
              variant="outline"
              class="h-8 w-8 p-0 border-n-slate-6 hover:bg-n-slate-3"
              :disabled="!table.getCanNextPage()"
              :title="t('CAMPAIGN.WHATSAPP_OFFICIAL.PAGINATION.GO_TO_NEXT')"
              @click="table.nextPage()"
            >
              <span class="sr-only">{{
                t('CAMPAIGN.WHATSAPP_OFFICIAL.PAGINATION.GO_TO_NEXT')
              }}</span>
              <span class="i-lucide-chevron-right h-4 w-4 text-n-slate-11" />
            </ShadcnButton>

            <ShadcnButton
              variant="outline"
              class="hidden h-8 w-8 p-0 lg:flex border-n-slate-6 hover:bg-n-slate-3"
              :disabled="!table.getCanNextPage()"
              :title="t('CAMPAIGN.WHATSAPP_OFFICIAL.PAGINATION.GO_TO_LAST')"
              @click="table.setPageIndex(table.getPageCount() - 1)"
            >
              <span class="sr-only">{{
                t('CAMPAIGN.WHATSAPP_OFFICIAL.PAGINATION.GO_TO_LAST')
              }}</span>
              <span class="i-lucide-chevrons-right h-4 w-4 text-n-slate-11" />
            </ShadcnButton>
          </div>
        </div>
      </div>
    </div>
  </div>
  <!-- eslint-enable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text, vue/no-static-inline-styles -->
</template>

<style scoped>
:deep([data-radix-select-trigger]) {
  @apply bg-n-slate-1 border-n-slate-6 text-n-slate-12;
}

:deep([data-radix-select-trigger]:focus) {
  @apply ring-2 ring-green-500 border-green-500;
}

:deep([data-radix-select-content]) {
  @apply bg-n-slate-1 border-n-slate-6 shadow-lg rounded-lg p-1;
}

:deep([data-radix-select-item]) {
  @apply text-n-slate-12 bg-n-slate-1 cursor-pointer rounded px-3 py-2 mx-0.5;
}

:deep([data-radix-select-item]:hover),
:deep([data-radix-select-item][data-highlighted]) {
  @apply bg-n-slate-3 text-n-slate-12;
}
</style>
